import { SseParser } from './parseSse'
import { normalizeStreamEvent, TutorStreamEvent } from './streamEvents'

export class StreamInterruptedError extends Error {
  constructor(message = 'The connection dropped before the answer finished.') {
    super(message)
    this.name = 'StreamInterruptedError'
  }
}

export class StreamIdleError extends Error {
  constructor() {
    super('The tutor stopped responding.')
    this.name = 'StreamIdleError'
  }
}

export interface ConsumeOptions {
  signal?: AbortSignal
  onEvent: (event: TutorStreamEvent) => void
  /** Heartbeat comments and any other bytes reset the idle watchdog. */
  onActivity?: () => void
  /** No bytes (heartbeats included) for this long => StreamIdleError. Server heartbeats every 15s. */
  idleTimeoutMs?: number
}

export const DEFAULT_IDLE_TIMEOUT_MS = 45_000

/**
 * Read an SSE Response body to completion.
 *
 * Resolves when a `done` or `error` event was delivered. Rejects with:
 *  - the AbortError when `signal` aborts (callers treat this as "Stopped");
 *  - StreamInterruptedError when the body ends without done/error (clean
 *    failure: there is no mid-answer resume, the UI offers Retry);
 *  - StreamIdleError when nothing (not even a heartbeat) arrives in time.
 */
export async function consumeTutorStream(
  response: Response,
  { signal, onEvent, onActivity, idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS }: ConsumeOptions,
): Promise<void> {
  const reader = response.body?.getReader()
  if (!reader) throw new Error('No response body')
  const decoder = new TextDecoder()
  let terminal = false
  let idleTimer: ReturnType<typeof setTimeout> | undefined
  let idleFired = false

  const armIdle = () => {
    if (idleTimer) clearTimeout(idleTimer)
    if (idleTimeoutMs <= 0) return
    idleTimer = setTimeout(() => {
      idleFired = true
      void reader.cancel().catch(() => undefined)
    }, idleTimeoutMs)
  }
  const onAbort = () => {
    void reader.cancel().catch(() => undefined)
  }
  signal?.addEventListener('abort', onAbort, { once: true })

  const parser = new SseParser({
    onComment: () => onActivity?.(),
    onFrame: (frame) => {
      let payload: unknown
      try {
        payload = JSON.parse(frame.data)
      } catch {
        return // malformed frame: skip rather than kill the answer
      }
      const event = normalizeStreamEvent(payload, frame.event)
      if (!event) return
      if (event.kind === 'done' || event.kind === 'error') terminal = true
      onEvent(event)
    },
  })

  try {
    armIdle()
    for (;;) {
      if (signal?.aborted) throw abortError()
      const { done, value } = await reader.read()
      if (signal?.aborted) throw abortError()
      if (done) break
      armIdle()
      onActivity?.()
      parser.feed(decoder.decode(value, { stream: true }))
    }
    parser.feed(decoder.decode())
    parser.end()
    if (idleFired) throw new StreamIdleError()
    if (!terminal) throw new StreamInterruptedError()
  } finally {
    if (idleTimer) clearTimeout(idleTimer)
    signal?.removeEventListener('abort', onAbort)
    try {
      reader.releaseLock()
    } catch {
      /* already released */
    }
  }
}

function abortError(): Error {
  const err = new Error('The operation was aborted.')
  err.name = 'AbortError'
  return err
}

export function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError'
}
